import { computed, ref, type Ref } from "vue";
import type { BatchProgress } from "@/services/starlinkMonitors";

export type BulkJobStatus = "running" | "done" | "failed";

export type BulkJobOutcome = {
  created: number;
  skipped: number;
  failed: number;
};

export type BulkJob = {
  id: string;
  label: string;
  successKey: string;
  countsKey: string;
  returnTo: string | null;
  total: number;
  done: number;
  status: BulkJobStatus;
  outcome: BulkJobOutcome | null;
  error: string | null;
  startedAt: number;
  finishedAt: number | null;
  // whatever the caller needs to show the run's own breakdown, attached once the run lands. it
  // sits on the job rather than in a callback because the page that started the run is torn
  // down the moment someone navigates away, and a callback into it would quietly do nothing.
  view: unknown;
};

// the caller gets this instead of a rejection, so a job nobody is watching can't turn into an
// unhandled rejection when it fails.
export type BulkJobResult<T> = { ok: true; value: T } | { ok: false; error: string };

export type BulkJobRequest<T> = {
  label: string;
  total: number;
  run: (onProgress: BatchProgress) => Promise<T>;
  summarize: (result: T) => BulkJobOutcome;
  // a clean run reads back as one sentence instead of three zeroes, and only the caller knows
  // whether its outcome.created means created or updated
  successKey?: string;
  // the same again for a run that didn't come back clean, because "created" is wrong on an update
  countsKey?: string;
  // where the run was started from, so the dock can put someone back on the page that has the
  // table and the result. a job without one is just text.
  returnTo?: string;
};

let nextId = 0;

export const createBulkJobs = () => {
  const jobs: Ref<BulkJob[]> = ref([]);

  const runningCount = computed(() => jobs.value.filter((job) => job.status === "running").length);

  const startJob = <T>({
    label,
    total,
    run,
    summarize,
    successKey = "starlink.bulkJobsAllCreated",
    countsKey = "starlink.bulkJobsCounts",
    returnTo,
  }: BulkJobRequest<T>) => {
    const job: BulkJob = {
      id: `bulk-${++nextId}`,
      label,
      successKey,
      countsKey,
      returnTo: returnTo ?? null,
      total,
      done: 0,
      status: "running",
      outcome: null,
      error: null,
      startedAt: Date.now(),
      finishedAt: null,
      view: null,
    };
    // push first, then work through what the array handed back. writing to the object we
    // built would skip the proxy and leave runningCount stale.
    jobs.value.push(job);
    const tracked = jobs.value[jobs.value.length - 1];

    const done = run((completed) => {
      tracked.done = Math.min(completed, tracked.total);
    }).then(
      (value): BulkJobResult<T> => {
        tracked.done = tracked.total;
        tracked.outcome = summarize(value);
        tracked.status = "done";
        tracked.finishedAt = Date.now();
        return { ok: true, value };
      },
      (error): BulkJobResult<T> => {
        const message = error?.message ?? "The job failed";
        tracked.done = tracked.total;
        tracked.error = message;
        tracked.status = "failed";
        tracked.finishedAt = Date.now();
        return { ok: false, error: message };
      }
    );

    return { id: job.id, done };
  };

  // a running job has nothing to fall back on if its row disappears, so the x only works
  // once it has landed.
  const dismissJob = (id: string) => {
    const index = jobs.value.findIndex((job) => job.id === id && job.status !== "running");
    if (index !== -1) {
      jobs.value.splice(index, 1);
    }
  };

  // the breakdown only exists once the run has landed, so the caller hangs it on afterwards
  const setJobView = (id: string, view: unknown) => {
    const job = jobs.value.find((item) => item.id === id);
    if (job) {
      job.view = view;
    }
  };

  // the dock asks by id and whoever owns the run answers. going through the store instead of a
  // callback means the page can be rebuilt after a navigation and still pick the request up.
  // the count is what makes asking for the same run twice in a row read as two requests.
  const viewRequest: Ref<{ id: string; count: number } | null> = ref(null);

  const requestView = (id: string) => {
    viewRequest.value = { id, count: (viewRequest.value?.count ?? 0) + 1 };
  };

  const clearFinished = () => {
    jobs.value = jobs.value.filter((job) => job.status === "running");
  };

  return {
    jobs,
    runningCount,
    startJob,
    setJobView,
    viewRequest,
    requestView,
    dismissJob,
    clearFinished,
  };
};

const shared = createBulkJobs();

export const useBulkJobs = () => shared;
